import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PaperPlaneTilt, ArrowClockwise, UsersThree, Sparkle, CheckCircle } from "@phosphor-icons/react";

/**
 * Hybrid stakeholder digest dialog.
 * On open we lazily fetch (and generate) an AI draft, show it editable, and let
 * the partner tick which stakeholders receive it. Send fires Resend server-side.
 *
 * Contract:
 *   - GET  /notes/{id}/stakeholder-digest       → {draft, candidates, history}
 *   - POST /notes/{id}/stakeholder-digest/regenerate  → fresh draft
 *   - POST /notes/{id}/stakeholder-digest/send  → {recipient_emails, subject, body_html}
 */
export default function StakeholderDigestDialog({ open, onClose, note, onSent }) {
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [history, setHistory] = useState([]);
  const [extraEmail, setExtraEmail] = useState("");

  useEffect(() => {
    if (!open || !note?.id) return;
    setLoading(true);
    api.get(`/notes/${note.id}/stakeholder-digest`)
      .then((r) => {
        setSubject(r.data.draft?.subject || "");
        setBodyHtml(r.data.draft?.body_html || "");
        setCandidates(r.data.candidates || []);
        setHistory(r.data.history || []);
        // Pre-check stakeholders marked as high-influence (partner reviews before send anyway).
        const preSelected = new Set((r.data.candidates || [])
          .filter((c) => (c.influence || "").toLowerCase() === "high")
          .map((c) => c.email));
        setSelected(preSelected);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load digest draft"))
      .finally(() => setLoading(false));
  }, [open, note?.id]);

  const toggle = (email) => {
    const next = new Set(selected);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    setSelected(next);
  };

  const addExtra = () => {
    const e = extraEmail.trim().toLowerCase();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) {
      toast.error("Enter a valid email");
      return;
    }
    if (!candidates.some((c) => c.email === e)) {
      setCandidates([...candidates, { name: e, title: "ad-hoc", email: e, influence: "" }]);
    }
    const next = new Set(selected); next.add(e); setSelected(next);
    setExtraEmail("");
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/notes/${note.id}/stakeholder-digest/regenerate`);
      setSubject(data.subject);
      setBodyHtml(data.body_html);
      toast.success("Draft regenerated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally { setRegenerating(false); }
  };

  const send = async () => {
    if (selected.size === 0) return toast.error("Pick at least one recipient");
    if (subject.trim().length < 3) return toast.error("Subject is too short");
    if (bodyHtml.trim().length < 20) return toast.error("Body is too short");
    setSending(true);
    try {
      const { data } = await api.post(`/notes/${note.id}/stakeholder-digest/send`, {
        recipient_emails: Array.from(selected),
        subject,
        body_html: bodyHtml,
      });
      toast.success(`Digest sent to ${data.send.recipients.length} stakeholder(s)`);
      setHistory([data.send, ...history]);
      onSent?.(data.send);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="rounded-none bg-card max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="stakeholder-digest-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <PaperPlaneTilt size={16} weight="fill" className="text-primary" />
            Stakeholder digest — partner-safe recap
          </DialogTitle>
        </DialogHeader>

        <div className="border-l-2 border-primary/60 bg-primary/5 p-3 text-xs text-muted-foreground leading-relaxed">
          <Sparkle size={12} weight="fill" className="text-primary inline mr-1" />
          YadidAi has drafted a partner-safe recap of this meeting. Review, edit, tick recipients, then send. Nothing leaves the firm until you click Send.
        </div>

        {loading && <div className="text-sm text-muted-foreground py-8 text-center">YadidAi is drafting a client-safe recap…</div>}

        {!loading && (
          <div className="space-y-4">
            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Subject</Label>
              <Input
                data-testid="digest-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="rounded-none bg-secondary/40 mt-1"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Body (HTML fragment)</Label>
                <button
                  onClick={regenerate}
                  disabled={regenerating}
                  data-testid="digest-regenerate"
                  className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow hover:text-eyebrow/80 inline-flex items-center gap-1 disabled:opacity-60"
                >
                  <ArrowClockwise size={11} weight="bold" /> {regenerating ? "Redrafting…" : "Ask YadidAi again"}
                </button>
              </div>
              <Textarea
                data-testid="digest-body"
                rows={10}
                value={bodyHtml}
                onChange={(e) => setBodyHtml(e.target.value)}
                className="rounded-none bg-secondary/40 mt-1 text-sm font-mono"
              />
              <div className="text-[0.625rem] text-muted-foreground mt-1">
                Wrapped in Yadid letterhead + footer before sending. Use <code>&lt;p&gt;</code>, <code>&lt;ul&gt;</code>, <code>&lt;li&gt;</code>.
              </div>
            </div>

            <div>
              <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                <UsersThree size={11} weight="bold" /> Recipients ({selected.size} selected)
              </Label>
              <div className="mt-2 border border-border divide-y divide-border" data-testid="digest-recipients">
                {candidates.length === 0 && (
                  <div className="px-3 py-4 text-xs text-muted-foreground">
                    No stakeholders on this partner organization yet — add them to the partner org, or type an ad-hoc email below.
                  </div>
                )}
                {candidates.map((c) => {
                  const checked = selected.has(c.email);
                  return (
                    <label key={c.email} className="flex items-center gap-3 px-3 py-2 text-xs cursor-pointer hover:bg-secondary/40">
                      <input
                        type="checkbox"
                        data-testid={`digest-recipient-${c.email}`}
                        checked={checked}
                        onChange={() => toggle(c.email)}
                        className="accent-primary"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{c.name} <span className="text-muted-foreground">· {c.email}</span></div>
                        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                          {c.title || "—"}{c.influence ? ` · ${c.influence} influence` : ""}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  data-testid="digest-extra-email"
                  value={extraEmail}
                  onChange={(e) => setExtraEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addExtra(); } }}
                  placeholder="Add ad-hoc email…"
                  className="rounded-none bg-secondary/40 flex-1"
                />
                <Button
                  onClick={addExtra}
                  data-testid="digest-extra-add"
                  variant="outline"
                  className="rounded-none border-border"
                >Add</Button>
              </div>
            </div>

            {history.length > 0 && (
              <div>
                <Label className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Previously sent</Label>
                <div className="mt-1 space-y-1" data-testid="digest-history">
                  {history.map((h) => (
                    <div key={h.id} className="text-[0.6875rem] text-muted-foreground flex items-center gap-2">
                      <CheckCircle size={11} weight="fill" className="text-emerald-500" />
                      <span>{String(h.sent_at).slice(0, 16).replace("T", " ")} UTC — {h.recipients.length} recipient(s)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="border-t border-border pt-3 mt-2">
          <Button variant="outline" onClick={onClose} className="rounded-none">Cancel</Button>
          <Button
            data-testid="digest-send"
            onClick={send}
            disabled={sending || loading || selected.size === 0}
            className="rounded-none bg-primary text-primary-foreground"
          >
            <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
            {sending ? "Sending…" : `Send to ${selected.size} stakeholder${selected.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
